/**
 * Every route in App.tsx is a `React.lazy` dynamic import of a content-hashed
 * chunk, and every push to `main` is a production deploy that replaces
 * `/assets/*` with newly hashed files and deletes the old ones. So a tab left
 * open across a deploy — which, at the rate this app ships, is most tabs —
 * asks for a chunk filename the CDN no longer has. The import rejects, the
 * lazy component throws during render, and with no error boundary React 18
 * unmounts the whole tree: an empty `#root` painted `#081326`. A blank navy
 * page, no message, no retry. Only a hard refresh fixes it, because only a
 * hard refresh re-reads `index.html` and learns the new hashes.
 *
 * This module decides two things, and nothing else, so both can be tested
 * without a browser: whether an error is that failure, and whether reloading
 * is safe. It imports nothing on purpose.
 */

/** sessionStorage key holding the time of the last automatic reload. */
export const RELOAD_MARK_KEY = "isyn.chunkReload.v1";

/**
 * A reload fixes a stale chunk on the first try. A second reload inside this
 * window means the fresh `index.html` is ALSO failing — a genuinely broken
 * deploy, not a stale tab — and reloading again would spin forever.
 */
export const RELOAD_COOLDOWN_MS = 30_000;

/**
 * What each engine says when a dynamic import cannot be fetched. Matched
 * case-insensitively against the error's message and name.
 *
 * Deliberately NOT here: the bare "failed to fetch" of a rejected `fetch()`.
 * Every SharePoint read in this app raises that when the network blips or a
 * throttle 302 is CORS-blocked, and treating one as a stale chunk would
 * reload the page out from under the user mid-work.
 */
const SIGNATURES: readonly string[] = [
  "failed to fetch dynamically imported module", // Chrome, Edge
  "error loading dynamically imported module",   // Firefox
  "importing a module script failed",            // Safari
  "unable to preload css for",                   // Vite's own preload helper
  "chunkloaderror",
];

/** True when `err` is a chunk that is no longer on the CDN. */
export function isChunkLoadError(err: unknown): boolean {
  if (err === null || err === undefined) return false;

  const parts: string[] = [];
  if (typeof err === "string") {
    parts.push(err);
  } else if (typeof err === "object") {
    const e = err as { message?: unknown; name?: unknown };
    if (typeof e.message === "string") parts.push(e.message);
    if (typeof e.name === "string") parts.push(e.name);
  }

  const haystack = parts.join(" ").toLowerCase();
  if (haystack === "") return false;
  return SIGNATURES.some((s) => haystack.includes(s));
}

/**
 * Whether an automatic reload is safe right now, given the mark previously
 * written to sessionStorage.
 *
 * `null` — no mark — is the documented first-run state and allows the reload.
 * A mark that will not parse is NOT: something wrote a value we cannot read,
 * and an unreadable clock is not permission to reload. Showing the user a
 * Reload button costs one click; guessing wrong costs an infinite loop.
 *
 * A mark in the future is treated the same way — the clock moved, so the
 * elapsed time it implies is not a measurement either.
 */
export function shouldAutoReload(
  mark: string | null,
  now: number,
  cooldownMs: number = RELOAD_COOLDOWN_MS,
): boolean {
  if (mark === null) return true;

  const at = Number(mark);
  if (mark.trim() === "" || !Number.isFinite(at)) return false;
  if (at > now) return false;

  return now - at >= cooldownMs;
}

/** The value to store once a reload is committed to. */
export function reloadMark(now: number): string {
  return String(now);
}
